import { z } from "zod";
import { emailSchema, isoDateTimeString, otpCodeSchema } from "./common";
import { userMeSchema } from "./users";

// ============================================================================
// Email + password (employer / admin web)
// ============================================================================
export const passwordSchema = z
  .string()
  .min(8, "Password must be ≥ 8 characters")
  .max(128, "Password too long")
  .refine((v) => /[A-Za-z]/.test(v), "Password must contain a letter")
  .refine((v) => /[0-9]/.test(v), "Password must contain a digit");

export const authRegisterRequestSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
  fullName: z.string().min(1).max(120),
  // LEGAL-08: the buyer must tick "Tôi đã đọc và đồng ý với Điều khoản sử
  // dụng, Thoả thuận dành cho Nhà tuyển dụng..." before an account is
  // created. The server rejects anything but true — the UI checkbox alone
  // is not trusted.
  consentAccepted: z.literal(true),
});

export const authRegisterResponseSchema = z.object({
  user: userMeSchema,
  accessToken: z.string().min(1),
  refreshToken: z.string().min(1),
});

// Two-step registration with inline email OTP. `start` validates the email is
// free and sends a 6-digit code without creating any row; `complete` re-checks
// the code, then creates the verified account. The password is held client-side
// between the two calls, so no pending/unverified account ever hits the DB.
export const authRegisterStartRequestSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
  fullName: z.string().min(1).max(120),
  // LEGAL-08: required from Start too, so an OTP can't be sent to someone who
  // hasn't ticked the checkbox. The actual consent write happens in Complete.
  consentAccepted: z.literal(true),
});

export const authRegisterStartResponseSchema = z.object({
  sentAt: isoDateTimeString,
  expiresAt: isoDateTimeString,
});

export const authRegisterCompleteRequestSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
  fullName: z.string().min(1).max(120),
  code: otpCodeSchema,
  // LEGAL-08: re-checked here — this is where the account (and the
  // TERMS_ACCEPT / EMPLOYER_AGREEMENT consent rows) are actually created.
  consentAccepted: z.literal(true),
});

export const authRegisterCompleteResponseSchema = authRegisterResponseSchema;

export const authLoginRequestSchema = z.object({
  email: emailSchema,
  password: z.string().min(1),
});

export const authLoginResponseSchema = z.object({
  user: userMeSchema,
  accessToken: z.string().min(1),
  refreshToken: z.string().min(1),
});

export const authEmailVerifyRequestVerifyResponseSchema = z.object({
  sentAt: isoDateTimeString,
  expiresAt: isoDateTimeString,
});

export const authEmailVerifyConfirmRequestSchema = z.object({
  token: z.string().min(8),
});

export const authEmailVerifyConfirmResponseSchema = z.object({
  emailVerifiedAt: isoDateTimeString,
});

export const authPasswordResetRequestSchema = z.object({
  email: emailSchema,
});

export const authPasswordResetResponseSchema = z.object({
  /** Always returns success to avoid leaking which emails exist. */
  ok: z.literal(true),
});

export const authPasswordResetConfirmRequestSchema = z.object({
  token: z.string().min(8),
  newPassword: passwordSchema,
});

export const authPasswordResetConfirmResponseSchema = z.object({
  ok: z.literal(true),
});

export const authChangePasswordRequestSchema = z.object({
  oldPassword: z.string().min(1),
  newPassword: passwordSchema,
});

export const authChangePasswordResponseSchema = z.object({
  ok: z.literal(true),
});

// Full OTP purpose set (all channels). PHONE_VERIFY is delivered via SCRUM-69
// phone infra and handled by the identity controller, not the email OTP route.
export const otpPurposeSchema = z.enum([
  "LOGIN",
  "RESET",
  "DELETE_ACCOUNT",
  "PHONE_VERIFY",
]);
// Email-only OTP purposes accepted by the /auth/otp/* endpoints. Deliberately
// excludes PHONE_VERIFY so a phone purpose can't be sent over the email route.
export const emailOtpPurposeSchema = z.enum([
  "LOGIN",
  "RESET",
  "DELETE_ACCOUNT",
]);
export const otpChannelSchema = z.enum(["EMAIL", "PHONE"]);

export const authOtpSendRequestSchema = z.object({
  email: emailSchema,
  purpose: emailOtpPurposeSchema,
});

export const authOtpSendResponseSchema = z.object({
  sentAt: isoDateTimeString,
  expiresAt: isoDateTimeString,
  /** Hint shown to user; currently always EMAIL in P1 v3. */
  channelHint: otpChannelSchema,
});

export const authOtpVerifyRequestSchema = z.object({
  email: emailSchema,
  code: otpCodeSchema,
  purpose: emailOtpPurposeSchema,
});

export const authOtpVerifyResponseSchema = z.object({
  user: userMeSchema,
  accessToken: z.string().min(1),
  refreshToken: z.string().min(1),
});

// Firebase Phone Auth flow: client (mobile) runs signInWithPhoneNumber via the
// Firebase SDK and receives an idToken. Backend's only job is to verify that
// idToken (signed by Firebase, contains phone_number claim) and issue a chotca
// session. No SMS is sent server-side.
export const authOtpFirebaseVerifyRequestSchema = z.object({
  idToken: z.string().min(1),
  purpose: otpPurposeSchema,
});

export const authOtpFirebaseVerifyResponseSchema = z.object({
  user: userMeSchema,
  accessToken: z.string().min(1),
  refreshToken: z.string().min(1),
});

// ============================================================================
// Social login — Google + Apple (worker app), docs/03-auth.md
// ============================================================================
// Direct verification: the client obtains a provider idToken natively
// (google-signin / expo-apple-authentication) and posts it here. The backend
// verifies it (Google certs / Apple JWKS), resolves a single chotca User via
// the AuthIdentity model, and issues the SAME session shape as OTP. Additive,
// parallel path — the OTP endpoints are unchanged.
export const oauthProviderSchema = z.enum(["GOOGLE", "APPLE"]);

export const authOAuthVerifyRequestSchema = z.object({
  provider: oauthProviderSchema,
  /** Google: idToken from GoogleSignin. Apple: identityToken (JWT). */
  idToken: z.string().min(1),
  /** Apple only: the RAW nonce; backend checks token.nonce == sha256(rawNonce). */
  rawNonce: z.string().min(1).optional(),
  /** Apple only, first sign-in: authorization code exchanged for a refresh
   *  token (stored to allow revoke-on-account-deletion). */
  authorizationCode: z.string().min(1).optional(),
});

export const authOAuthVerifyResponseSchema = z.object({
  user: userMeSchema,
  accessToken: z.string().min(1),
  refreshToken: z.string().min(1),
});

export const authRefreshRequestSchema = z.object({
  refreshToken: z.string().min(1),
});

export const authRefreshResponseSchema = z.object({
  accessToken: z.string().min(1),
  refreshToken: z.string().min(1),
});

export const authLogoutRequestSchema = z.object({
  refreshToken: z.string().min(1).optional(),
});

export const authLogoutResponseSchema = z.object({
  ok: z.literal(true),
});

export type OtpPurpose = z.infer<typeof otpPurposeSchema>;
export type OtpChannel = z.infer<typeof otpChannelSchema>;
export type AuthOtpSendRequest = z.infer<typeof authOtpSendRequestSchema>;
export type AuthOtpSendResponse = z.infer<typeof authOtpSendResponseSchema>;
export type AuthOtpVerifyRequest = z.infer<typeof authOtpVerifyRequestSchema>;
export type AuthOtpVerifyResponse = z.infer<typeof authOtpVerifyResponseSchema>;
export type AuthOtpFirebaseVerifyRequest = z.infer<
  typeof authOtpFirebaseVerifyRequestSchema
>;
export type AuthOtpFirebaseVerifyResponse = z.infer<
  typeof authOtpFirebaseVerifyResponseSchema
>;
export type OAuthProvider = z.infer<typeof oauthProviderSchema>;
export type AuthOAuthVerifyRequest = z.infer<
  typeof authOAuthVerifyRequestSchema
>;
export type AuthOAuthVerifyResponse = z.infer<
  typeof authOAuthVerifyResponseSchema
>;
export type AuthRefreshRequest = z.infer<typeof authRefreshRequestSchema>;
export type AuthRefreshResponse = z.infer<typeof authRefreshResponseSchema>;
export type AuthLogoutRequest = z.infer<typeof authLogoutRequestSchema>;
export type AuthLogoutResponse = z.infer<typeof authLogoutResponseSchema>;
export type AuthRegisterRequest = z.infer<typeof authRegisterRequestSchema>;
export type AuthRegisterResponse = z.infer<typeof authRegisterResponseSchema>;
export type AuthRegisterStartRequest = z.infer<
  typeof authRegisterStartRequestSchema
>;
export type AuthRegisterStartResponse = z.infer<
  typeof authRegisterStartResponseSchema
>;
export type AuthRegisterCompleteRequest = z.infer<
  typeof authRegisterCompleteRequestSchema
>;
export type AuthRegisterCompleteResponse = z.infer<
  typeof authRegisterCompleteResponseSchema
>;
export type AuthLoginRequest = z.infer<typeof authLoginRequestSchema>;
export type AuthLoginResponse = z.infer<typeof authLoginResponseSchema>;
export type AuthEmailVerifyRequestVerifyResponse = z.infer<
  typeof authEmailVerifyRequestVerifyResponseSchema
>;
export type AuthEmailVerifyConfirmRequest = z.infer<
  typeof authEmailVerifyConfirmRequestSchema
>;
export type AuthEmailVerifyConfirmResponse = z.infer<
  typeof authEmailVerifyConfirmResponseSchema
>;
export type AuthPasswordResetRequest = z.infer<
  typeof authPasswordResetRequestSchema
>;
export type AuthPasswordResetResponse = z.infer<
  typeof authPasswordResetResponseSchema
>;
export type AuthPasswordResetConfirmRequest = z.infer<
  typeof authPasswordResetConfirmRequestSchema
>;
export type AuthPasswordResetConfirmResponse = z.infer<
  typeof authPasswordResetConfirmResponseSchema
>;
export type AuthChangePasswordRequest = z.infer<
  typeof authChangePasswordRequestSchema
>;
export type AuthChangePasswordResponse = z.infer<
  typeof authChangePasswordResponseSchema
>;
